import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, onSnapshot, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

export type UserStatus = 'pending' | 'approved' | 'denied';
export type UserRole = 'user' | 'admin';

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  status: UserStatus;
  role: UserRole;
  createdAt: string;
}

interface AuthContextType {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  emailVerified: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Listen to profile changes
        console.log("Checking profile for UID:", firebaseUser.uid);
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        
        const unsubscribeProfile = onSnapshot(userDocRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as UserProfile;
            setProfile(data);
            
            // Sync profile email after a verifyBeforeUpdateEmail link has been confirmed
            if (firebaseUser.emailVerified && firebaseUser.email && firebaseUser.email !== data.email) {
              updateDoc(doc(db, 'users', firebaseUser.uid), { email: firebaseUser.email })
                .catch((e) => console.error('Email sync failed', e));
            }
          } else {
            // Profile doesn't exist yet, might be in the middle of registration
            setProfile(null);
          }
          setLoading(false);
        }, (error) => {
          // Ignore permission denied errors if the user is logging out and auth is null
          if (error.code === 'permission-denied' && !auth.currentUser) {
            console.log("Profile unsubscribed cleanly on logout");
          } else {
            console.error("Profile fetch error:", error);
          }
          setLoading(false);
        });

        return () => unsubscribeProfile();
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const logout = async () => {
    localStorage.removeItem('oracle_screener_results');
    await auth.signOut();
  };

  const isAdmin = profile?.role === 'admin';
  const isApproved = profile?.status === 'approved' || isAdmin;
  const emailVerified = user?.emailVerified ?? false;

  return (
    <AuthContext.Provider value={{ user, profile, loading, isAdmin, isApproved, emailVerified, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
